import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ImpersonationSession {
  id: string;
  impersonated_user_id: string | null;
  impersonated_role: string | null;
  impersonated_department: string | null;
  expires_at: string;
  reason: string | null;
}

interface ImpersonationContextType {
  activeSession: ImpersonationSession | null;
  loading: boolean;
  isImpersonating: boolean;
  startImpersonation: (params: {
    impersonatedUserId?: string;
    impersonatedRole?: string;
    impersonatedDepartment?: string;
    reason?: string;
    durationMinutes?: number;
  }) => Promise<{ success: boolean; data?: any; error?: any }>;
  endImpersonation: () => Promise<{ success: boolean; error?: any }>;
  getRemainingTime: () => number;
  refresh: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeSession, setActiveSession] = useState<ImpersonationSession | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchActiveSession = async () => {
    const adminId = user?.id;
    const t0 = performance.now();
    console.log('[ImpersonationProvider] fetchActiveSession:start', { adminId });

    if (!user) {
      setActiveSession(null);
      setLoading(false);
      console.log('[ImpersonationProvider] fetchActiveSession:skip (no user)');
      return;
    }

    try {
      // Moderate timeout (5s) - don't block indefinitely but allow time for real queries
      const rpcCall = supabase.rpc('get_active_impersonation', { _admin_user_id: user.id });
      const timeout = new Promise<{ data: any; error: any }>((resolve) =>
        setTimeout(() => resolve({ data: null, error: new Error('timeout') }), 5000)
      );

      const { data, error } = await Promise.race([rpcCall as any, timeout]);

      if (error && (error as any)?.message !== 'timeout') {
        console.error('[ImpersonationProvider] Error fetching session:', error);
        setActiveSession(null);
      } else if ((error as any)?.message === 'timeout') {
        console.warn('[ImpersonationProvider] RPC timeout (5s), proceeding without impersonation');
        setActiveSession(null);
      } else {
        const session = data?.[0] || null;
        setActiveSession(session);
        console.log('[ImpersonationProvider] fetchActiveSession:success', { hasSession: !!session, session });
      }
    } catch (err) {
      console.error('[ImpersonationProvider] Exception:', err);
      setActiveSession(null);
    } finally {
      // ALWAYS unlock, no matter what
      setLoading(false);
      const dt = Math.round(performance.now() - t0);
      console.log('[ImpersonationProvider] fetchActiveSession:end', { durationMs: dt });
    }
  };

  useEffect(() => {
    // Safety timeout to prevent indefinite loading (10s - very generous)
    const safetyTimeout = setTimeout(() => {
      console.warn('[ImpersonationProvider] Safety timeout (10s) - forcing loading=false');
      setLoading(false);
    }, 10000);

    fetchActiveSession().finally(() => clearTimeout(safetyTimeout));

    // Check every minute for expiration
    const interval = setInterval(fetchActiveSession, 60000);

    return () => {
      clearTimeout(safetyTimeout);
      clearInterval(interval);
    };
  }, [user?.id]);

  const startImpersonation = async (params: {
    impersonatedUserId?: string;
    impersonatedRole?: string;
    impersonatedDepartment?: string;
    reason?: string;
    durationMinutes?: number;
  }) => {
    try {
      const { data, error } = await supabase.functions.invoke('manage-impersonation', {
        body: {
          action: 'start',
          ...params
        }
      });

      if (error) throw error;

      await fetchActiveSession();
      return { success: true, data };
    } catch (error) {
      console.error('Error starting impersonation:', error);
      return { success: false, error };
    }
  };

  const endImpersonation = async () => {
    if (!activeSession) return { success: false, error: 'No active session' };

    try {
      const { error } = await supabase.functions.invoke('manage-impersonation', {
        body: {
          action: 'end',
          sessionId: activeSession.id
        }
      });

      if (error) throw error;

      setActiveSession(null);
      return { success: true };
    } catch (error) {
      console.error('Error ending impersonation:', error);
      return { success: false, error };
    }
  };

  const getRemainingTime = () => {
    if (!activeSession) return 0;
    
    const expiresAt = new Date(activeSession.expires_at).getTime();
    const now = Date.now();
    const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000 / 60)); // minutes
    
    return remaining;
  };

  const value = {
    activeSession,
    loading,
    isImpersonating: !!activeSession,
    startImpersonation,
    endImpersonation,
    getRemainingTime,
    refresh: fetchActiveSession
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonationContext() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonationContext must be used within an ImpersonationProvider');
  }
  return context;
}
